import { types, Instance, flow, getEnv } from "mobx-state-tree";

import { Endpoint, endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { permissionsService } from "../authorization/UserPermissionsService";
import { ViewSewerObject, ViewSewerObjectFromAssignedProject } from "../authorization/Permissions";
import { StructureDetails } from "./StructureDetails";
import { StructureDetailsResponse } from "../../api-models/response/StructureDetailsResponse";
import { SewerObjectParentEntityFromPath } from "./SewerObjectParentEntityFromPath";
import { CurrentSewerObjectStorageModel } from "../storage/CurrentSewerObjectStorageModel";

const CurrentStructureModel = types.model({
  structureId: types.frozen<number>(),
  currentStructure: types.optional(types.frozen<StructureDetails>(), new StructureDetails()),
  parentEntityFromPath: types.frozen<SewerObjectParentEntityFromPath>(),
  isDeleteInProgress: types.optional(types.boolean, false),
});

export const CurrentStructureStorageModel = types
  .compose(CurrentStructureModel, CurrentSewerObjectStorageModel, StorageModel)
  .named("CurrentStructureStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: StructureDetailsResponse) {
        self.currentStructure = new StructureDetails(response);
      },
      delete: flow(function* () {
        self.isDeleteInProgress = true;
        const endpoint = endpoints.get("structure");
        endpoint.routeParams = [self.structureId];
        const response = yield getEnv(self).api.delete(endpoint);
        self.isDeleteInProgress = false;
        return response;
      }),
    };
  })
  .views((self) => ({
    get structureIsLoaded() {
      return self.currentStructure.id > 0;
    },

    get endpoint() {
      const endpoint: Endpoint = endpoints.get("structure");
      endpoint.routeParams = [self.structureId];
      return endpoint;
    },

    get canUserViewStructure() {
      return (
        permissionsService.userHasPermissionTo(ViewSewerObject) ||
        permissionsService.userHasPermissionTo(ViewSewerObjectFromAssignedProject)
      );
    },
  }))
  .views((self) => ({
    get canLoadItems() {
      return self.canUserViewStructure && self.structureId > 0;
    },
    get isLoading() {
      return !self.structureIsLoaded;
    },
  }));

export type CurrentStructureStorage = Instance<typeof CurrentStructureStorageModel>;
